import { motion } from "framer-motion";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Layers, Globe, Cog, Award } from "lucide-react";
import ParticleField from "../components/ParticleField";
import ParticleWordmark from "../components/ParticleWordmark";
import HeroSpotlight from "../components/HeroSpotlight";
import TiltProductCard from "../components/TiltProductCard";
import EcosystemDiagram from "../components/EcosystemDiagram";
import AnimatedCounter from "../components/AnimatedCounter";
import ContactForm from "../components/ContactForm";
import MagneticButton from "../components/MagneticButton";
import GeoHeroLine from "../components/GeoHeroLine";
import MerchantLogoStrip from "../components/MerchantLogoStrip";
import SEOHead, { orgSchema, websiteSchema } from "../components/SEOHead";
import { analytics } from "../lib/analytics";
import { PRODUCT_ORDER, PRODUCTS } from "../lib/products";

const Landing = () => {
  const { t } = useTranslation();
  const heroRef = useRef(null);

  const pillars = [
    { key: "ecosystem", icon: Layers },
    { key: "regions", icon: Globe },
    { key: "innovation", icon: Sparkles },
    { key: "scalable", icon: Cog },
  ];

  return (
    <div className="grain-overlay">
      <SEOHead
        title="One company. Six solutions to grow your business"
        description="TRYB is a unified SaaS portfolio: digital loyalty cards, online reviews, digital business cards, link-in-bio pages, appointment booking and gamified lead capture. Used by SMBs across the USA, Morocco, Spain and Turkey."
        path="/"
        schemas={[orgSchema(), websiteSchema()]}
      />
      <HeroSpotlight containerRef={heroRef} />
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-center pt-32 pb-24 overflow-hidden" data-testid="hero-section">
        <div className="hero-mesh" aria-hidden="true" />
        <div className="absolute inset-0 grid-bg opacity-50" aria-hidden="true" />
        <ParticleField color="180, 200, 255" density={0.00009} />
        {/* Ambient TRYB wordmark made of particles — softly behind the main hero copy */}
        <ParticleWordmark className="opacity-30 md:opacity-40" opacity={0.4} />

        <div className="relative w-full min-w-0 mx-auto max-w-7xl px-5 md:px-8">
          <GeoHeroLine />
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-[10px] md:text-xs font-bold uppercase tracking-[0.32em] text-muted-foreground"
            data-testid="hero-eyebrow"
          >
            {t("hero.eyebrow")}
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.2, 0.7, 0.2, 1] }}
            className="font-display mt-5 max-w-5xl text-[2.4rem] sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl leading-[1.04] sm:leading-[1.02] tracking-tighter break-words"
            data-testid="hero-h1"
          >
            {t("hero.h1Pre")}
            <br />
            <span className="text-gradient-brand">{t("hero.h1Highlight")}</span>{" "}
            <span className="text-foreground/90">{t("hero.h1Post")}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="mt-8 max-w-2xl text-base md:text-lg text-muted-foreground leading-relaxed"
            data-testid="hero-sub"
          >
            {t("hero.sub")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <MagneticButton>
              <a
                href="#solutions"
                className="btn-primary"
                data-testid="hero-cta-primary"
                onClick={() => analytics.ctaClick("hero_explore_solutions", "hero")}
              >
                {t("hero.primary")}
                <ArrowRight className="h-4 w-4" />
              </a>
            </MagneticButton>
            <MagneticButton>
              <a
                href="#contact"
                className="btn-ghost"
                data-testid="hero-cta-secondary"
                onClick={() => analytics.ctaClick("hero_contact_sales", "hero")}
              >
                {t("hero.secondary")}
              </a>
            </MagneticButton>
          </motion.div>

          {/* Floating product chips */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-16 flex flex-wrap items-center gap-2.5"
            data-testid="hero-product-chips"
          >
            {PRODUCT_ORDER.map((slug) => {
              const p = PRODUCTS[slug];
              const Icon = p.icon;
              return (
                <Link
                  to={`/${slug}`}
                  key={slug}
                  className="group flex items-center gap-2 rounded-full border border-foreground/10 bg-foreground/[0.03] px-3.5 py-1.5 text-xs font-medium hover:bg-foreground/[0.07] transition"
                  data-testid={`hero-chip-${slug}`}
                >
                  <span
                    className="flex h-5 w-5 items-center justify-center rounded-full"
                    style={{ background: `${p.accentColor}33` }}
                  >
                    <Icon className="h-3 w-3" style={{ color: p.accentColor }} />
                  </span>
                  {p.name}
                </Link>
              );
            })}
          </motion.div>

          <MerchantLogoStrip />
        </div>
      </section>

      {/* SOLUTIONS GRID */}
      <section id="solutions" className="relative py-24 md:py-32" data-testid="solutions-section">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="max-w-2xl">
            <p className="text-[10px] md:text-xs font-bold uppercase tracking-[0.24em] text-muted-foreground">
              {t("solutions.eyebrow")}
            </p>
            <h2 className="font-display mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
              {t("solutions.title")}
            </h2>
            <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed">
              {t("solutions.sub")}
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {PRODUCT_ORDER.map((slug, i) => (
              <div key={slug}>
                <TiltProductCard product={PRODUCTS[slug]} index={i} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ECOSYSTEM */}
      <section className="relative py-24 md:py-32 overflow-hidden" data-testid="ecosystem-section">
        <div className="absolute inset-0 grid-bg opacity-30" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 md:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-[10px] md:text-xs font-bold uppercase tracking-[0.24em] text-muted-foreground">
              {t("ecosystem.eyebrow")}
            </p>
            <h2 className="font-display mt-4 text-4xl md:text-5xl tracking-tighter leading-[1.05]">
              {t("ecosystem.title")}
            </h2>
            <p className="mt-5 text-base text-muted-foreground leading-relaxed">
              {t("ecosystem.sub")}
            </p>
          </div>

          <div className="mt-14 md:mt-20">
            <EcosystemDiagram />
          </div>
        </div>
      </section>

      {/* TRUST PILLARS */}
      <section className="relative py-24 md:py-32" data-testid="trust-section">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <div className="max-w-2xl">
            <p className="text-[10px] md:text-xs font-bold uppercase tracking-[0.24em] text-muted-foreground">
              {t("trust.eyebrow")}
            </p>
            <h2 className="font-display mt-4 text-4xl md:text-5xl tracking-tighter leading-[1.05]">
              {t("trust.title")}
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {pillars.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.key}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  className="rounded-2xl border border-foreground/10 bg-foreground/[0.02] p-7 hover:bg-foreground/[0.04] transition"
                  data-testid={`pillar-${p.key}`}
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-foreground/5 border border-foreground/10">
                    <Icon className="h-5 w-5 text-foreground/90" strokeWidth={1.6} />
                  </div>
                  <h3 className="font-display mt-6 text-xl">{t(`trust.pillars.${p.key}.title`)}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    {t(`trust.pillars.${p.key}.text`)}
                  </p>
                </motion.div>
              );
            })}
          </div>

          {/* Stats row */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { v: 4750, s: "+" },
              { v: 20, s: "+" },
              { v: 8000000, s: "+" },
              { v: 6, s: "" },
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.05 * i }}
                className="rounded-2xl border border-foreground/10 bg-foreground/[0.02] p-6"
                data-testid={`stat-${i}`}
              >
                <p className="font-display text-3xl md:text-4xl text-gradient-brand">
                  <AnimatedCounter value={stat.v} suffix={stat.s} />
                </p>
                <p className="mt-2 text-xs text-muted-foreground uppercase tracking-wider">{t("landingStats", { returnObjects: true })[i]}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT + CONTACT */}
      <section id="about" className="relative py-24 md:py-32" data-testid="about-section">
        <div className="mx-auto max-w-7xl px-5 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="text-[10px] md:text-xs font-bold uppercase tracking-[0.24em] text-muted-foreground">
              {t("about.eyebrow")}
            </p>
            <h2 className="font-display mt-4 text-4xl md:text-5xl tracking-tighter leading-[1.05]">
              {t("about.title")}
            </h2>
            <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed">
              {t("about.text")}
            </p>
            <p className="mt-4 text-xs text-muted-foreground/70">
              {t("about.legal")}
            </p>

            <div className="mt-12 lg:hidden">
              <ContactForm />
            </div>
          </div>

          <div className="hidden lg:block lg:col-span-7">
            <div id="contact">
              <p className="text-[10px] md:text-xs font-bold uppercase tracking-[0.24em] text-muted-foreground">
                {t("contact.eyebrow")}
              </p>
              <h3 className="font-display mt-4 text-4xl tracking-tighter">{t("contact.title")}</h3>
              <p className="mt-3 text-base text-muted-foreground max-w-md">{t("contact.sub")}</p>
              <div className="mt-8">
                <ContactForm />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Landing;

